import React from 'react';
import { Box, VStack, HStack, Heading, Badge, Button, Stack, } from '@chakra-ui/react';
import COLORS from '../../../constants/colors';
import { useTranslation } from 'react-i18next';
import { BusyIndicator } from '../../../components/utility/busy-indicator';
import Placeholder from '../../../components/utility/placeholder';
import { tKeys } from '../../../translations/keys';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FONTS from '../../../constants/fonts';
import Text from '../../../components/content/text';
import Icon from '../../../components/content/icon';
import { t } from 'i18next';
const SuggestionCard = ({ title, content, type, colorScheme = COLORS.Govrn.Blue.T500.value, onInsert, }) => (<Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50" _hover={{
        bg: COLORS.Govrn.Blue.T50.value,
        borderColor: COLORS.Govrn.Blue.T200.value,
        transform: 'translateY(-1px)',
        boxShadow: 'sm',
    }} transition="all 0.2s">
    <HStack mb={2} justify="space-between">
      <Heading size="sm" color="gray.700">
        {title}
      </Heading>
      <Badge color={colorScheme} fontSize="xs" px={2} py={1} borderRadius="full">
        {type}
      </Badge>
    </HStack>
    <Text font={FONTS.T1.T12Px.Regular400.value} color={COLORS.Text.T400.value} whiteSpace="pre-line">
      {content}
    </Text>
    <Button size="sm" color={colorScheme} width="full" onClick={() => onInsert(content)} _hover={{ transform: 'translateY(-1px)' }} transition="all 0.2s">
      {t(tKeys.minutes.sidebar.template.insert)}
    </Button>
  </Box>);
const AI_SUGGESTIONS = [
    {
        title: t(tKeys.minutes.sidebar.template.meeting_opening),
        content: t(tKeys.minutes.sidebar.template.meeting_opening_content),
        type: t(tKeys.minutes.sidebar.template.type),
    },
    {
        title: t(tKeys.minutes.sidebar.template.attendance_record),
        content: t(tKeys.minutes.sidebar.template.attendance_record_content),
        type: t(tKeys.minutes.sidebar.template.type),
    },
    {
        title: 'Previous Minutes',
        content: 'The minutes of the previous meeting held on [date] were reviewed and approved.',
        type: 'Template',
    },
    {
        title: 'Action Items',
        content: 'Action Items:\n1. [Action Item] - Assigned to: [Name], Due: [Date]\n2. [Action Item] - Assigned to: [Name], Due: [Date]',
        type: 'Template',
    },
];
const MEETING_INFO = {
    header: 'Q1 2024 Board Meeting - 2024-03-15',
    location: 'Virtual Meeting',
    attendees: 'John Smith, Alice Johnson, Bob Wilson',
    agenda: 'Previous Minutes Review, Financial Performance Q1, Strategic Planning,...',
};
export const Sidebar = ({ editor, event, isAdmin, eventSummary, eventId, showPreviewMinutes, eventShouldBeRecorded, hasBeenRecorded = true, eventIsPast, }) => {
    // Attributes
    const { t } = useTranslation();
    // const { isModuleAvailable, supportEmail, handleMailto } =
    //   useEventDetailsAISummaryLogic();
    const formattedSummary = eventSummary
        ? eventSummary.replace(/\\n/gi, '\n')
        : null;
    // Debug the formatted content
    // More specific renderers with better defaults
    const Heading1 = ({ children }) => (<Text font={FONTS.T1.T16Px.Bold700.value} color={COLORS.Text.T500.value} mt="1rem" mb="0.5rem" numberOfLines={100}>
      {children}
    </Text>);
    const Heading2 = ({ children }) => (<Text font={FONTS.T1.T16Px.SemiBold600.value} color={COLORS.Text.T500.value} mt="1rem" mb="0.5rem" numberOfLines={100}>
      {children}
    </Text>);
    const List = ({ children, ordered }) => (<Box ml="1rem" pb="0.5rem">
      {children}
    </Box>);
    const ListItem = ({ children }) => (<HStack spacing={2} mb="0.25rem" align="flex-start">
      <Box as="span" w="4px" h="4px" minWidth="4px" minHeight="4px" borderRadius="full" bg={COLORS.Text.T400.value} mt="0.5rem"/>
      <Text font={FONTS.T1.T14Px.Regular400.value} color={COLORS.Text.T400.value} numberOfLines={100}>
        {children}
      </Text>
    </HStack>);
    const Paragraph = ({ children }) => (<Text font={FONTS.T1.T14Px.Regular400.value} color={COLORS.Text.T400.value} mb="0.75rem" lineHeight="1.5" numberOfLines={100}>
      {children}
    </Text>);
    const Strong = ({ children }) => (<Text font={FONTS.T1.T14Px.Bold.value} color={COLORS.Text.T500.value} numberOfLines={100} as="span">
      {children}
    </Text>);
    const Emphasis = ({ children }) => (<Text font={FONTS.T1.T14Px.SemiBold600.value} color={COLORS.Text.T400.value} numberOfLines={100} as="span">
      {children}
    </Text>);
    // Text component for plain text fallback
    const TextComponent = ({ children }) => (<Text font={FONTS.T1.T14Px.Regular400.value} color={COLORS.Text.T400.value} numberOfLines={100} mb="0.5rem">
      {children}
    </Text>);
    // Render
    return (<Stack pb={'2rem'} width={'100%'} overflow={'auto'} height={'95vh'} position={'sticky'} top={0}>
      <VStack width="full" spacing={1}>
        <Box width="full" height="1px" backgroundColor={COLORS.Line.value}/>

        <BusyIndicator isEmpty={!eventSummary || !eventShouldBeRecorded} placeholder={<Placeholder title={hasBeenRecorded
                ? t(tKeys.event.details.AI.placeholder.title)
                : eventIsPast
                    ? t(tKeys.event.details.AI.placeholder.title_not_recorded)
                    : t(tKeys.event.details.AI.placeholder.title_will_not_recorded)} imageType={'monocle-emoji'} subTitle={hasBeenRecorded
                ? t(tKeys.event.details.AI.placeholder.title, {
                    supportEmail: 'support@govrn.io',
                })
                : t(tKeys.event.details.AI.placeholder.title_will_not_recorded)} hasImage={true} endEnhancer={!eventShouldBeRecorded && (<Box pt={'1.25rem'}>
                    <Button colorScheme="blue" size="sm" onClick={() => (window.location.href = 'mailto:support@govrn.io')}>
                      {t(tKeys.common.send_email) || 'Contact Support'}
                    </Button>
                  </Box>)}/>}>
          {eventShouldBeRecorded && (<Box p="1.25rem" bgColor={COLORS.White.T500.value} width="full">
              {/* AI Generated Badge Header */}
              <Box mb={4} p={2} bg={COLORS.Govrn.Blue.T50.value} borderRadius="md" borderLeft={`4px solid ${COLORS.Govrn.Blue.T500.value}`}>
                <HStack spacing={2} align="center">
                  <Icon name="ai-assist" height={20} width={20}/>
                  <Text font={FONTS.T1.T12Px.Regular400.value} color={COLORS.Text.T400.value}>
                    {t(tKeys.minutes.sidebar.ai_generated_badge) ||
                'Generated by Govrn AI Meeting Recorder'}
                  </Text>
                </HStack>
              </Box>

              {formattedSummary ? (<ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                    h1: Heading1,
                    h2: Heading2,
                    h3: Heading2,
                    h4: Heading2,
                    h5: Heading2,
                    h6: Heading2,
                    p: Paragraph,
                    ul: ({ children }) => (<List ordered={false}>{children}</List>),
                    ol: ({ children }) => (<List ordered={true}>{children}</List>),
                    li: ListItem,
                    strong: Strong,
                    em: Emphasis,
                    a: props => (<a style={{ color: COLORS.Govrn.Blue.T500.value }} {...props}/>),
                    text: TextComponent,
                }}>
                  {formattedSummary}
                </ReactMarkdown>) : (<Text>{t(tKeys.minutes.sidebar.no_summary_available)}</Text>)}
            </Box>)}
        </BusyIndicator>
      </VStack>
    </Stack>);
};
